import { useEffect, useState } from 'react'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import type { Battle, MasteryGain } from '@mezha/api'
import { useShellMode } from '../../shell/ShellContext'
import { Emblem } from '../../ui/brand/Emblems'
import { GameWindow, Gauge, PlankButton, cx } from '../../ui'
import { useHeroQuery } from '../../api/endpoints/hero'
import { useWorldQuery } from '../../api/endpoints/world'
import { countdown, useServerNow } from '../../lib/time'
import { RewardRow } from '../common'
import { dropDraft } from './draft'
import { FullLog } from './RoundLog'
import { reasonText } from './words'

/** Where «Продолжить» leads when the battle was not opened from a screen. */
const BACK: Record<Battle['kind'], string> = { pve: '/play/map', tutorial: '/play', duel: '/play/arena', team: '/play/arena' }

/** The ending as the server confirmed it: outcome, reason, reward and the opened seed. */
export function BattleResult({ battle: b }: { battle: Battle }) {
  useShellMode('window')
  const navigate = useNavigate()
  const from = (useLocation().state as { from?: string } | null)?.from
  const [tab, setTab] = useState('result')
  useEffect(() => dropDraft(b.id), [b.id])

  const r = b.result
  const me = b.actors.find(a => a.you)
  const hero = useHeroQuery().data
  const now = useServerNow(1000)
  // A defeat on the map or in the story injures and carries the hero to the healer (owner 28.09.2026).
  const injury = r && !r.won && b.kind === 'pve' ? hero?.injury ?? null : null
  const place = useWorldQuery().data?.place_id
  const healer = injury ? injury.healers.find(h => h.place_id === place) ?? null : null
  // After a defeat the hero is at the healer: «Продолжить» leads to the place.
  const back = injury ? '/play' : from && !from.startsWith('/play/battle') ? from : BACK[b.kind]
  const where = back.startsWith('/play/mentor/') ? 'к наставнику' : back === '/play/arena' ? 'на арену' : back === '/play/map' ? 'на карту' : 'в локацию'
  const title = !r ? 'Бой завершён' : r.draw ? 'Ничья' : r.won ? 'Победа' : 'Поражение'
  const mastery = r && r.mastery.length > 0 ? r.mastery : null
  return (
    <GameWindow title="Итог боя" emblem="arena.duel" idBase="result" tabs={[{ id: 'result', label: 'Итог' }, { id: 'log', label: 'Журнал боя' }]}
      activeTab={tab} onTab={setTab} bodyClassName="battle-result"
      action={<PlankButton gold onClick={() => navigate(back)} aria-label={`Продолжить: ${where}`}>Продолжить ▸</PlankButton>}>
      {tab === 'result' && (
        <div className={cx('result', mastery && 'result--v2')}>
          <div className="result__main">
          <h3 className="result__title">{title}</h3>
          <img className="result__flourish" src="/assets/ui/divider.webp" alt="" aria-hidden="true" />
          {!r && <p className="result__sub" role="status">Подводим итоги боя…</p>}
          {r && <p className="result__sub">{b.title ? `${b.title}: ` : ''}{reasonText(r.outcome, r.won, r.draw)}.</p>}
          {me && (
            <div className="result__hero parch">
              <b>{me.name}</b> после боя
              <Gauge kind="hp" value={me.hp} max={me.hp_max} />
              {me.mn_max > 0 && <Gauge kind="mn" value={me.mn} max={me.mn_max} />}
              <small>{b.kind === 'tutorial' ? 'Учебный бой: здоровье и мана героя не изменились.' : 'Здоровье и мана записаны в героя.'}</small>
            </div>
          )}
          {r && (
            <>
              <div className="eyebrow">{r.reward ? 'Ваша награда' : 'Без награды'}</div>
              {r.reward && <RewardRow reward={r.reward} />}
              {!r.reward && !r.won && <p className="note">Награду дают только за победу.</p>}
              {injury && (
                <p className="note result__injury" role="status"><Emblem name="injury.heavy" size={16} />
                  {injury.heavy ? 'Тяжёлая травма' : 'Травма'} на {countdown(now, injury.until).text}: {healer ? `вас отнесли к лекарю — ${healer.name} (${healer.place_name})` : 'лекарей пока нет рядом'}.
                  {injury.heavy ? ' Пока она тяжёлая, идти и драться нельзя.' : ` В бою −${injury.weaken_pct}% здоровья и силы удара.`}
                </p>
              )}
              {b.rated && <p className="note"><Emblem name="arena.rated" size={16} /> Рейтинговый бой — место в доске почёта обновится на арене.</p>}
            </>
          )}
          </div>
          {mastery && <div className="result__side"><MasteryGains gains={mastery} /></div>}
        </div>
      )}
      {tab === 'log' && <FullLog battle={b} />}
    </GameWindow>
  )
}

/** The growth of the schools in the battle (combat_system_v2.md §9.2): the experience, the new point of mastery. */
function MasteryGains({ gains }: { gains: MasteryGain[] }) {
  return (
    <div className="parch result__mastery">
      <div className="result__h"><Emblem name="nav.skills" size={18} />Мастерство школ</div>
      {gains.map(g => {
        const span = Math.max(1, g.next_xp - g.level_xp)
        const now = g.next_xp ? Math.min(100, Math.round(((g.xp_total - g.level_xp) / span) * 100)) : 100
        const added = g.level > g.level_before ? now : Math.min(now, Math.round((g.xp / span) * 100))
        return (
          <div key={g.school_id} className="mgain">
            {g.icon ? <img className="em" src={g.icon} alt="" width={30} height={30} /> : <Emblem name="nav.skills" size={30} />}
            <b>{g.name}</b><span className="mgain__gain">+{g.xp}</span>
            <div className="mbar" title="опыт школы до следующего очка мастерства" aria-hidden="true">
              <span className="mbar__was" style={{ width: `${now - added}%` }} />
              <span className="mbar__add" style={{ left: `${now - added}%`, width: `${added}%` }} />
            </div>
            <small>
              {g.level > g.level_before ? <b className="mgain__up">Мастерство {g.level_before} → {g.level}.</b> : <>Мастерство {g.level}. </>}
              {g.level >= g.cap ? ` Потолок уровня — ${g.cap}: опыт копится до нового уровня.` : g.next_xp ? ` До ${g.level + 1} — ещё ${g.next_xp - g.xp_total} опыта школы.` : ''}
            </small>
          </div>
        )
      })}
      <Link className="linkbtn linkbtn--ink" to="/play/skills">Книга умений ▸</Link>
    </div>
  )
}
